import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaEnvelope, FaArrowLeft, FaPaperPlane } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import logo from '../assets/logo.png';
import { showToast } from '../components/ui/Toast';
import './ForgotPassword.css';
import api from '../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await api.post('/users/send-otp/', { email });
      const data = response.data;
      
      if (data.success) {
        setCodeSent(true);
        setSuccess('Verification code sent to your email');
        setCountdown(60);
        showToast.success('Verification code sent successfully!');
      } else {
        throw new Error(data.message || 'Failed to send verification code');
      }
    } catch (error) {
      const errorMsg = error.message || 'Failed to send verification code. Please try again.';
      setError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendCode = (e) => {
    e.preventDefault();
    if (countdown > 0) return;
    setCountdown(60);
    handleSendCode(e);
  };

  const handleContinue = () => {
    navigate('/reset-password-otp', { state: { email } });
  };

  return (
    <Layout>
      <div className="forgot-password-page-container">
        <div className="forgot-password-split-screen">
          <div className="forgot-password-left-panel">
            <div className="forgot-password-content">
              <img src={logo} alt="UniTrade Logo" className="forgot-password-logo" />
              <h2>Reset Your Password</h2>
              <p>Enter your email address to receive a verification code for resetting your password.</p>
            </div>
          </div>
          
          <div className="forgot-password-right-panel">
            <div className="forgot-password-form-container">
              <button 
                type="button" 
                className="back-button" 
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft /> Back
              </button>
              
              <h3>Forgot Password</h3>
              <p className="forgot-subtitle">Enter your email to receive a verification code</p>
              
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              
              <form onSubmit={handleSendCode} className="forgot-password-form">
                <div className="input-group-forgot">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-with-icon">
                    <FaEnvelope className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading || codeSent}
                      required
                    />
                  </div>
                </div>
                
                {!codeSent ? (
                  <button 
                    type="submit" 
                    className="forgot-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Verification Code <FaPaperPlane className="ms-2" />
                      </>
                    )}
                  </button>
                ) : (
                  <div className="mt-4">
                    <button
                      type="button"
                      className="forgot-btn"
                      onClick={handleContinue}
                    >
                      Continue
                    </button>
                    <div className="text-center">
                      <button
                        type="button"
                        className="resend-link"
                        onClick={handleResendCode}
                        disabled={countdown > 0}
                      >
                        {countdown > 0 
                          ? `Resend code in ${countdown}s` 
                          : "Didn't receive a code? Resend"}
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="text-center mt-4">
                  <p className="forgot-subtitle mb-0">
                    Remember your password?{' '}
                    <Link 
                      to="/login" 
                      className="resend-link"
                      style={{ textDecoration: 'underline' }}
                    >
                      Back to Login
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ForgotPassword;
